import { User } from 'oidc-client-ts';
import { BASKET_API, BASKET_VERSION, QUOTE_API, VERSION } from './constants';
import { ApiResponse } from './types/api-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Account,
  BasketResponse,
  BasketSection,
  DateType,
  Quote,
  QuoteAddress,
  QuoteDates,
  GenericErrorResponse,
  QuoteExtra,
  QuoteExtras,
  QuoteParcels,
  QuoteResponse,
  QuoteError,
  Basket,
  BasketItem,
  AvailableExtra,
  QuoteProtection,
  BasketQuote,
  QuoteCountry,
  Legal,
  QuoteExtrasCalculations,
  BasketMeta
} from './types/basket-types';
import { authenticatedFetchWithTenantAnalytics } from './fetch';
import { BasketSections } from './types/enums';
import { getCurrentItemId } from './getCurrentItemId';
import { parseParcelsUrlString } from './utils/parse-parcels-url-string';
import { BasketContextActions } from '~/providers/basketContext';
import { Dispatch } from 'react';
import { EventEmitter } from 'stream';
import { captureException } from './metrics';
import { onlyUniqueNonBlank } from './utils/array';
import { CompanyStatus } from './types/customs';
import { AddressLookup, SavedAddress } from './types/edge-api-types';
import {
  filterQuoteDatesWithExtraPrices,
  filterQuoteDatesWithoutExtraPrices,
  getQuotesValidCollectionDates,
  quoteDateCollectionTimeOptions,
  quoteRequiresCollectionTime
} from './quotes';
import { OrderLine } from './types/orderline';
import { getCookieValues, parseAbTastyCookie } from './analytics';
import { Label, Protection, Upsell } from './types/extra-types';
import { getFingerprint } from './fingerprint';
import { setTimeOnDateElement } from './utils/date-utils';

async function getBasketId(
  tenant: string,
  settings: { [key: string]: string },
  session: string,
  user: User | null | undefined
) {
  const key = `basket_${tenant}`;
  let id = localStorage.getItem(key);
  const itemId = getCurrentItemId(location?.search);

  if (id && id.split('_')[0] === BASKET_VERSION) {
    return { id: id as string, itemId } as const;
  }

  try {
    const response = await authenticatedFetchWithTenantAnalytics(
      user,
      tenant,
      settings,
      session,
      `${BASKET_API}/${tenant}/basket/create?prefix=${BASKET_VERSION}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create basket id invalid http response ${response.status}`);
    }

    const result = (await response.json()) as ApiResponse<string>;
    if (!result?.success || !result?.result) {
      throw new Error(`Failed to create basket id invalid api response ${result.error?.message}`);
    }

    id = result.result;
    localStorage.setItem(key, id);
    return { id: id as string, itemId } as const;
  } catch (e) {
    captureException(e, 'Failed to create basket id', {
      section: 'basket'
    });
    throw new Error('Failed to create basket id');
  }
}

function clearBasketId(tenant: string) {
  localStorage.removeItem(`basket_${tenant}`);
}

export async function loadBasket(
  tenant: string,
  settings: { [key: string]: string },
  session: string,
  user: User | null | undefined,
  retry?: boolean
) {
  const basketId = await getBasketId(tenant, settings, session, user);

  try {
    const response = await authenticatedFetchWithTenantAnalytics(
      user,
      tenant,
      settings,
      session,
      `${BASKET_API}/${tenant}/basket/${basketId.id}`
    );

    if (!response.ok) {
      throw new Error(`Failed to load basket invalid http response ${response.status}`);
    }

    const basket: BasketResponse = (await response.json()) as BasketResponse;
    if (!basket?.success || !basket?.result?.items) {
      if (basket.error?.message === 'Basket not found') {
        if (user && !retry) {
          clearBasketId(tenant);
          return await loadBasket(tenant, settings, session, user, true);
        }
        return { ...basketId, basket: null };
      } else if (!retry && basket.error?.code && ['400.2', '401.1', '401.2', '401.3'].includes(basket.error?.code)) {
        clearBasketId(tenant);
        return await loadBasket(tenant, settings, session, user, true);
      }
      throw new Error(`Failed to load basket invalid api response ${basket.error?.message}`);
    }

    return { ...basketId, basket: basket.result };
  } catch (e) {
    captureException(e, 'Failed to load basket', {
      section: 'basket'
    });
  }

  return { ...basketId, basket: null };
}

export async function getBasketItemQuotes(
  tenant: string,
  settings: { [key: string]: string },
  basketId: string,
  basketItemId: string,
  sessionId: string,
  user: User | null | undefined
): Promise<{ quotes: BasketQuote[]; errors: QuoteError[]; etag: string | null }> {
  const quoteResponse = await authenticatedFetchWithTenantAnalytics(
    user,
    tenant,
    settings,
    sessionId,
    `${QUOTE_API}/quote/basket/${basketId}?itemIds=${basketItemId}`
  );

  if (!quoteResponse.ok && quoteResponse.status === 400) {
    const errors = (await quoteResponse.json()) as GenericErrorResponse;
    return { quotes: [], errors: errors.issues, etag: null };
  }

  if (!quoteResponse.ok) {
    throw new Error(`Failed to fetch quote invalid http response ${quoteResponse.status}`);
  }

  const quote = (await quoteResponse.json()) as QuoteResponse;
  if (!quote?.success || !quote?.result) {
    throw new Error(`Failed to fetch quote invalid api response ${quote.error?.message}`);
  }

  // Now do prohib
  return {
    quotes: quote.result.map((q) => ({ ...q, currency: quote.currency })),
    errors: [],
    etag: quoteResponse.headers.get('Etag')
  };
}

export async function storeBasketQuote(
  tenant: string,
  settings: { [key: string]: string },
  user: User | null | undefined,
  session: string,
  basketId: string,
  basketItemId: string,
  body: BasketSection
) {
  const response = await authenticatedFetchWithTenantAnalytics(
    user,
    tenant,
    settings,
    session,
    `${BASKET_API}/${tenant}/basket/${basketId}/${basketItemId}/${BasketSections.QUOTE}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update quote section invalid http response ${response.status}`);
  }

  const result = (await response.json()) as BasketResponse;
  if (!result?.success || !result.result?.items[basketItemId]?.sections[BasketSections.QUOTE]) {
    throw new Error(`Failed to update quote section invalid api response ${result.error?.message}`);
  }

  return result.result;
}

export function createBasketItem(
  account: Account | undefined,
  defaultAddress: SavedAddress | undefined,
  quote: Quote,
  searchParams: { [k: string]: string },
  extras: string[],
  termsAccepted: boolean,
  addParcelContents: boolean,
  destinationAddress: AddressLookup | undefined | null
): { [name: string]: BasketSection } {
  const {
    cp: originPostcode,
    col: originCountry,
    dest: destinationCountry,
    dp: destinationPostcode,
    p: parcelsText
  } = searchParams;

  const combinedExtras = (extras || []).filter(onlyUniqueNonBlank);

  const origin: QuoteAddress =
    defaultAddress && (!originPostcode || originPostcode === defaultAddress.postcode)
      ? {
          contactname: defaultAddress.name,
          county: defaultAddress.county,
          postcode: defaultAddress.postcode,
          street: defaultAddress.street,
          town: defaultAddress.town,
          property: defaultAddress.property,
          country: originCountry,
          email: account?.email || defaultAddress.email,
          phone: defaultAddress?.phone,
          companyStatus: CompanyStatus.PERSONAL,
          metadata: { type: 'SAVED', isDefaultSavedAddress: !!defaultAddress.isDefault }
        }
      : {
          country: originCountry,
          postcode: originPostcode,
          email: account?.email,
          contactname: account?.forename && account?.surname ? `${account.forename} ${account.surname}` : undefined,
          companyStatus: CompanyStatus.PERSONAL
        };

  const collectionDates = getQuotesValidCollectionDates(quote);
  const collectionDatesWithoutUpsell = collectionDates.filter(filterQuoteDatesWithoutExtraPrices);
  const collectionDatesWithUpsell = collectionDates.filter(filterQuoteDatesWithExtraPrices);
  const collectionDate = collectionDatesWithoutUpsell[0] || collectionDatesWithUpsell[0];
  const requiresCollectionTime = quoteRequiresCollectionTime(quote);
  const availableCollectionTimes =
    requiresCollectionTime && collectionDate ? quoteDateCollectionTimeOptions(collectionDate) : [];

  origin.metadata = { ...origin.metadata, basedOnAccount: !account?.email };

  // Add any extras if they exist
  if (collectionDate && collectionDate.price && collectionDate.extraKey) {
    combinedExtras.push(collectionDate.extraKey);
  }

  const initialParcels = parseParcelsUrlString(parcelsText, addParcelContents, origin.country || 'GBR');

  return {
    [BasketSections.ACCOUNT]: { payload: { ...account } },
    [BasketSections.QUOTE]: { payload: { ...quote } },
    [BasketSections.SERVICE]: { payload: { slug: quote.service.slug } },
    [BasketSections.PARCELS]: {
      payload: {
        parcels: initialParcels,
        initialParcels: initialParcels.length
      } as QuoteParcels
    },
    [BasketSections.ORIGIN]: { payload: origin },
    [BasketSections.DESTINATION]: {
      payload: {
        country: destinationAddress?.countryIso || destinationCountry,
        postcode: destinationPostcode || destinationAddress?.postcode,
        companyStatus: CompanyStatus.PERSONAL,
        email: searchParams['destinationEmail'],
        contactname: searchParams['destinationName'],
        phone: searchParams['destinationPhone'],
        companyTaxNumber: searchParams['destinationCompanyTaxNumber'],
        eori: searchParams['destinationEori'],
        taxId: searchParams['destinationTaxId'],
        organisation: destinationAddress?.organisation,
        locality: destinationAddress?.locality,
        county: destinationAddress?.county,
        street: destinationAddress?.street,
        town: destinationAddress?.town,
        property: destinationAddress?.property,
        metadata: destinationAddress ? { type: 'PARSED', id: destinationAddress.id } : undefined
      } as QuoteAddress
    },
    [BasketSections.EXTRAS_CALCULATIONS]: { payload: {} as QuoteExtrasCalculations },
    [BasketSections.EXTRAS]: {
      payload: {
        extras: combinedExtras
          ? combinedExtras.map((e) => {
              return { key: e, metadata: {} } as QuoteExtra;
            })
          : [],
        initialExtras: combinedExtras
          ? combinedExtras.map((e) => {
              return { key: e, metadata: {} } as QuoteExtra;
            })
          : [],
        userInteractedWithProtection: false
      } as QuoteExtras
    },
    [BasketSections.DATES]: {
      payload: {
        dates:
          collectionDate && collectionDate.collection
            ? [
                {
                  type: DateType.Collection,
                  date:
                    requiresCollectionTime && availableCollectionTimes.length > 0
                      ? setTimeOnDateElement(collectionDate, availableCollectionTimes[0])
                      : collectionDate.collection.date
                }
              ]
            : []
      } as QuoteDates
    },
    [BasketSections.LEGAL]: {
      payload: {
        termsAccepted: termsAccepted
      } as Legal
    }
  };
}

export function notifyBasketUpdate(
  currentItemId: string,
  dispatch: Dispatch<BasketContextActions>,
  broadcast: BroadcastChannel,
  events: EventEmitter,
  basket: Basket
) {
  dispatch({
    type: 'UPDATE_SECTIONS',
    id: currentItemId,
    sections: basket.items[currentItemId]?.sections
  });

  for (const section of Object.getOwnPropertyNames(basket.items[currentItemId].sections)) {
    events.emit('basket_section_saved', {
      id: currentItemId,
      name: section,
      section: basket.items[currentItemId]?.sections[section]
    });

    broadcast.postMessage({
      type: 'basket_section_saved',
      id: currentItemId,
      section: section,
      payload: basket.items[currentItemId]?.sections[section]
    });
  }
}

export function basketSelectedExtras(protection: QuoteProtection, quote: Quote, basket: BasketItem): AvailableExtra[] {
  const e = basket.sections[BasketSections.EXTRAS]?.payload as QuoteExtras;
  if (e) {
    return e.extras
      .map((extra) => {
        return (
          protection.options.find((qExtra) => qExtra.key === extra.key) ||
          quote.availableExtras.find((qExtra) => qExtra.key === extra.key)
        );
      })
      .filter((x) => x !== undefined && x !== null) as AvailableExtra[];
  }
  return [];
}

export const getCountryRedirect = (address: QuoteAddress, countries: QuoteCountry[]) => {
  if (!address?.postcode || !countries || !countries.length) return undefined;
  return countries
    .find((country) => country.iso3Code === address.country && country.redirects)
    ?.redirects?.find(
      (redirect) =>
        redirect.postcodeRegex &&
        address.postcode?.toUpperCase().match(redirect.postcodeRegex) &&
        countries.some((country) => country.iso3Code === redirect.iso3Code && country.iso2Code === redirect.iso2Code)
    );
};

export const getCountrySwitchResult = (
  address: QuoteAddress,
  countries: QuoteCountry[]
): { shouldRedirect: boolean; serviceSupportsCountry: boolean; redirect?: string | undefined } => {
  if (!address?.postcode || !countries || !countries.length)
    return { shouldRedirect: false, serviceSupportsCountry: true };

  const redirect = countries
    .find((country) => country.iso3Code === address.country && country.redirects)
    ?.redirects?.find(
      (redirect) => redirect.postcodeRegex && address.postcode?.toUpperCase().match(redirect.postcodeRegex)
    );

  if (!redirect) return { shouldRedirect: false, serviceSupportsCountry: true };

  return {
    shouldRedirect: true,
    serviceSupportsCountry: countries.some(
      (country) => country.iso3Code === redirect.iso3Code && country.iso2Code === redirect.iso2Code
    ),
    redirect: redirect.iso3Code
  };
};

export const getExtraFromOrderlineExtras = (extra: string) => {
  switch (extra) {
    case 'Extra Comp. Cover':
      return Protection.FULL;
    case 'Extended Base Cover':
      return Protection.BASE;
    case 'Driver Brings Label':
      return Label.DRIVER;
    case 'Print In Store':
      return Label.STORE;
    case 'Delivery Alert':
      return Upsell.SMS;
    case 'Signature':
      return Upsell.SIGNATURE;
    case 'Delivery Guarantee':
      return Upsell.GUARANTEE;
    default:
      return undefined;
  }
};

export function mapOrderlineExtras(orderLine: OrderLine, quote: Quote) {
  const extras: QuoteExtra[] = [];
  if (
    orderLine.extras.some((e) => e === 'Extra Comp. Cover') &&
    quote.availableExtras.some((x) => x.key === Protection.FULL)
  ) {
    extras.push({ key: Protection.FULL, metadata: {} });
  } else if (
    orderLine.extras.some((e) => e === 'Extended Base Cover') &&
    quote.availableExtras.some((x) => x.key === Protection.BASE)
  ) {
    extras.push({ key: Protection.BASE, metadata: {} });
  }

  if (
    orderLine.extras.some((e) => e === 'Driver Brings Label') &&
    quote.availableExtras.some((x) => x.key === Label.DRIVER)
  ) {
    extras.push({ key: Label.DRIVER, metadata: {} });
  }

  if (
    orderLine.extras.some((e) => e === 'Print In Store') &&
    quote.availableExtras.some((x) => x.key === Label.STORE)
  ) {
    extras.push({ key: Label.STORE, metadata: {} });
  }

  if (orderLine.extras.some((e) => e === 'Delivery Alert') && quote.availableExtras.some((x) => x.key === Upsell.SMS)) {
    extras.push({ key: Upsell.SMS, metadata: {} });
  }

  if (
    orderLine.extras.some((e) => e === 'Signature') &&
    quote.availableExtras.some((x) => x.key === Upsell.SIGNATURE)
  ) {
    extras.push({ key: Upsell.SIGNATURE, metadata: {} });
  }

  if (
    orderLine.extras.some((e) => e === 'Delivery Guarantee') &&
    quote.availableExtras.some((x) => x.key === Upsell.GUARANTEE)
  ) {
    extras.push({ key: Upsell.GUARANTEE, metadata: {} });
  }
  return extras;
}

export async function createReorderBasketItem(
  orderLine: OrderLine,
  quote: Quote,
  analytics = { id: '', qs: '', session: '', path: '', referrer: '' }
) {
  let host: string | undefined = undefined;
  if (analytics.referrer) {
    try {
      host = new URL(analytics.referrer)?.hostname;
    } catch (e) {
      host = undefined;
    }
  }

  const collectionDates = getQuotesValidCollectionDates(quote);
  const collectionDatesWithoutUpsell = collectionDates.filter(filterQuoteDatesWithoutExtraPrices);
  const collectionDatesWithUpsell = collectionDates.filter(filterQuoteDatesWithExtraPrices);
  const collectionDate = collectionDatesWithoutUpsell[0] || collectionDatesWithUpsell[0];

  const extras = mapOrderlineExtras(orderLine, quote);

  // Add any extras if they exist
  if (collectionDate && collectionDate.price && collectionDate.extraKey) {
    extras.push({ key: collectionDate.extraKey, metadata: {} });
  }

  return {
    [BasketSections.ACCOUNT]: {
      payload: {
        email: orderLine.account.email,
        forename: orderLine.account.forename,
        surname: orderLine.account.surname
      } as Account
    },
    [BasketSections.QUOTE]: { payload: { ...quote } as Quote },
    [BasketSections.SERVICE]: { payload: { slug: orderLine.service.slug } },
    [BasketSections.PARCELS]: {
      payload: {
        parcels: orderLine.parcels.map((parcel) => {
          return {
            id: uuidv4(),
            height: parcel.height ?? 1,
            length: parcel.length ?? 1,
            width: parcel.width ?? 1,
            weight: parcel.weight ?? 1,
            value: parcel.value ?? 1,
            contentSummary: parcel.contents,
            contents: parcel.customs.map((contents) => {
              return {
                id: uuidv4(),
                quantity: contents.quantity,
                description: contents.description,
                value: contents.unitValue,
                countryOfManufacture: contents.countryOfManufacture,
                tariffCode: contents.tariffCode
              };
            })
          };
        })
      } as QuoteParcels
    },
    [BasketSections.ORIGIN]: {
      payload: {
        country: orderLine.origin.country.key,
        postcode: orderLine.origin.postcode,
        email: orderLine.origin.email,
        contactname: orderLine.origin.contactName,
        companyStatus: orderLine.customs.vatStatus,
        organisation: orderLine.origin.organisation,
        property: orderLine.origin.property,
        street: orderLine.origin.street,
        town: orderLine.origin.town,
        companyTaxNumber: orderLine.customs.taxNumber,
        county: orderLine.origin.county,
        eori: orderLine.parcels
          .map((x) => x.customs.map((y) => y.senderTaxId))
          .flat()
          .filter((x) => x)[0],
        locality: orderLine.origin.locality,
        phone: orderLine.origin.phone,
        taxId: orderLine.customs.iOSSCode
      } as QuoteAddress
    },
    [BasketSections.DESTINATION]: {
      payload: {
        country: orderLine.destination.country.key,
        postcode: orderLine.destination.postcode,
        email: orderLine.destination.email,
        contactname: orderLine.destination.contactName,
        companyStatus: orderLine.customs.recipientTaxNumber ? CompanyStatus.REGISTERED : CompanyStatus.PERSONAL,
        organisation: orderLine.destination.organisation,
        property: orderLine.destination.property,
        street: orderLine.destination.street,
        town: orderLine.destination.town,
        companyTaxNumber: orderLine.customs.recipientTaxNumber,
        county: orderLine.destination.county,
        eori: orderLine.parcels
          .map((x) => x.customs.map((y) => y.recipientTaxNumber))
          .flat()
          .filter((x) => x)[0],
        locality: orderLine.destination.locality,
        phone: orderLine.destination.phone
      } as QuoteAddress
    },
    [BasketSections.EXTRAS_CALCULATIONS]: { payload: {} as QuoteExtrasCalculations },
    [BasketSections.EXTRAS]: {
      payload: {
        extras: extras,
        initialExtras: [],
        userInteractedWithProtection: false
      } as QuoteExtras
    },
    [BasketSections.DATES]: {
      payload: {
        dates:
          collectionDate && collectionDate.collection
            ? [
                {
                  type: DateType.Collection,
                  date: collectionDate.collection.date
                }
              ]
            : []
      } as QuoteDates
    },
    [BasketSections.LEGAL]: {
      payload: {
        termsAccepted: true
      } as Legal
    },
    [BasketSections.META]: {
      payload: {
        staging: false,
        completed: true,
        created: new Date().toISOString(),
        metadata: {
          device: analytics.id,
          qs: analytics.qs ?? undefined,
          referrer: host,
          session: analytics.session,
          path: analytics.path ?? undefined,
          fp: await getFingerprint(),
          appVersion: VERSION,
          ...getCookieValues(['_ga']),
          ...parseAbTastyCookie()
        }
      } as BasketMeta
    }
  };
}

export function getBasketCollectionDate(data: QuoteDates) {
  if (!data?.dates || !Array.isArray(data.dates)) {
    return undefined;
  }

  return data.dates.find((date) => date?.type === DateType.Collection)?.date;
}
